import React from 'react';
import { FieldError } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  error?: FieldError;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children?: React.ReactNode;
  className?: string;
}

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps & React.InputHTMLAttributes<HTMLInputElement>>(({
  label,
  name,
  type = 'text',
  placeholder,
  required = false,
  autoComplete,
  autoFocus = false,
  error,
  value,
  onChange,
  children,
  className,
  ...inputProps
}, ref) => {
  return (
    <div className={cn('space-y-2', className)}>
      <Label 
        htmlFor={name} 
        className="text-sm font-medium text-neutral-700 transition-colors duration-200"
      >
        {label}
        {required && <span className="text-semantic-error ml-1">*</span>}
      </Label>
      
      <div className="relative group">
        {children || (
          <Input
            ref={ref}
            id={name}
            name={name}
            type={type}
            autoComplete={autoComplete}
            required={required}
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            className={cn(
              'mt-1 transition-all duration-200',
              'focus:ring-2 focus:ring-brand-primary focus:border-brand-primary focus:ring-offset-1',
              'hover:border-neutral-400',
              'group-hover:shadow-sm',
              error && 'border-semantic-error focus:ring-semantic-error'
            )}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? `${name}-error` : undefined}
            {...inputProps}
          />
        )}
      </div>
      
      {error && (
        <p 
          id={`${name}-error`}
          className="text-sm text-semantic-error mt-1"
          role="alert"
        >
          {error.message}
        </p>
      )}
    </div>
  );
});

FormField.displayName = 'FormField';
